import type { ICart } from "../queries/cart-gql";
import { useMemo } from "react";
import type { ICalculatedDraftOrder } from "../api/calculateDraftOrderV2";

// calculatedOrder는 INTL 스토어에서만 사용
export default function useCartInfo(cart: ICart["cart"], calculatedOrder?: ICalculatedDraftOrder) {
  // 총 상품 갯수
  const totalQuantity = useMemo(() => cart.totalQuantity, [cart]);

  const originTotalAmount = useMemo(() => parseFloat(cart.cost.checkoutChargeAmount.amount), [cart]);

  // 주문 상품의 원래 가격의 총 합
  const subTotalAmount = useMemo(() => parseFloat(cart.cost.subtotalAmount.amount), [cart]);

  // 상시 할인
  const autoDiscountAmount = useMemo(() => {
    if (calculatedOrder) {
      return calculatedOrder.platformDiscounts
        .filter((item) => item.automaticDiscount)
        .filter((item) => !item.discountClasses.includes("SHIPPING"))
        .reduce((prev, curr) => prev + parseFloat(curr.totalAmountPriceSet.shopMoney.amount), 0);
    }

    const orderDiscount = cart.discountAllocations
      .filter(
        (item) =>
          item.__typename === "CartAutomaticDiscountAllocation" || item.__typename === "CartCustomDiscountAllocation",
      )
      .reduce((prev, curr) => prev + parseFloat(curr.discountedAmount.amount), 0);

    const productDiscount = cart.lines.edges
      .flatMap(({ node }) => node.discountAllocations)
      .filter(
        (item) =>
          item.__typename === "CartAutomaticDiscountAllocation" || item.__typename === "CartCustomDiscountAllocation",
      )
      .reduce((prev, curr) => prev + parseFloat(curr.discountedAmount.amount), 0);

    return orderDiscount + productDiscount;
  }, [cart, calculatedOrder]);

  // 상시 할인 이름
  const autoDiscountTitle = useMemo(() => {
    const titles = new Set<string>();

    if (calculatedOrder) {
      calculatedOrder.platformDiscounts.forEach((item) => {
        if (item.automaticDiscount && !item.discountClasses.includes("SHIPPING")) {
          titles.add(item.title);
        }
      });
    } else {
      cart.discountAllocations.forEach((item) => {
        if (
          item.__typename === "CartAutomaticDiscountAllocation" ||
          item.__typename === "CartCustomDiscountAllocation"
        ) {
          titles.add(item.title);
        }
      });

      cart.lines.edges
        .flatMap(({ node }) => node.discountAllocations)
        .forEach((item) => {
          if (
            item.__typename === "CartAutomaticDiscountAllocation" ||
            item.__typename === "CartCustomDiscountAllocation"
          ) {
            titles.add(item.title);
          }
        });
    }

    if (titles.has("Employee")) {
      return "Employee";
    }

    if (titles.has("Friends")) {
      return "Friends";
    }

    return Array.from(titles).join(", ");
  }, [cart, calculatedOrder]);

  // 할인코드 할인
  const codeDiscountAmount = useMemo(() => {
    if (calculatedOrder) {
      return calculatedOrder.platformDiscounts
        .filter((item) => !item.automaticDiscount)
        .reduce((prev, curr) => prev + parseFloat(curr.totalAmountPriceSet.shopMoney.amount), 0);
    }

    const orderDiscount = cart.discountAllocations
      .filter((item) => item.__typename === "CartCodeDiscountAllocation")
      .reduce((prev, curr) => prev + parseFloat(curr.discountedAmount.amount), 0);

    const productDiscount = cart.lines.edges
      .flatMap(({ node }) => node.discountAllocations)
      .filter((item) => item.__typename === "CartCodeDiscountAllocation")
      .reduce((prev, curr) => prev + parseFloat(curr.discountedAmount.amount), 0);

    return orderDiscount + productDiscount;
  }, [cart, calculatedOrder]);

  // 할인코드 이름
  const codeDiscountTitle = useMemo(() => {
    const titles = new Set<string>();

    if (calculatedOrder) {
      calculatedOrder.platformDiscounts.forEach((item) => {
        if (!item.automaticDiscount) {
          titles.add(item.title);
        }
      });
    } else {
      cart.discountAllocations.forEach((item) => {
        if (item.__typename === "CartCodeDiscountAllocation") {
          titles.add(item.code);
        }
      });

      cart.lines.edges
        .flatMap(({ node }) => node.discountAllocations)
        .forEach((item) => {
          if (item.__typename === "CartCodeDiscountAllocation") {
            titles.add(item.code);
          }
        });
    }

    return Array.from(titles).join(", ");
  }, [cart, calculatedOrder]);

  // 총 결제 금액
  const totalAmount = useMemo(() => {
    if (calculatedOrder) {
      return parseFloat(calculatedOrder.totalPriceSet.shopMoney.amount);
    }

    return parseFloat(cart.cost.totalAmount.amount);
  }, [cart, calculatedOrder]);

  // 총 부가세 금액
  const taxAmount = useMemo(() => {
    if (calculatedOrder) return parseFloat(calculatedOrder.taxLines[0]?.priceSet.shopMoney.amount || "0");
    return 0;
  }, [calculatedOrder]);

  // 총 관세 금액
  const dutyAmount = useMemo(() => {
    if (calculatedOrder) {
      return (
        parseFloat(calculatedOrder.totalTaxSet.shopMoney.amount || "0") -
        parseFloat(calculatedOrder.taxLines[0]?.priceSet.shopMoney.amount || "0")
      );
    }

    return 0;
  }, [calculatedOrder]);

  // 배송비 정보
  const shippingInfo = useMemo(
    () =>
      cart.deliveryGroups.edges.length
        ? cart.deliveryGroups.edges.flatMap((edge) =>
            edge.node.deliveryOptions.filter((option) => option.deliveryMethodType === "SHIPPING"),
          )[0]
        : null,
    [cart],
  );

  // 총 배송비 금액
  const shippingAmount = useMemo(() => {
    if (calculatedOrder) {
      return parseFloat(calculatedOrder.shippingLine?.discountedPriceSet.shopMoney.amount || "0");
    }

    return parseFloat(shippingInfo?.estimatedCost.amount || "0");
  }, [calculatedOrder, shippingInfo]);

  const orderName = useMemo(() => {
    if (cart.lines.edges.length === 0) return "";

    return cart.lines.edges.length > 1
      ? `${cart.lines.edges[0].node.merchandise.product.title}외 ${cart.lines.edges.length - 1}건`
      : cart.lines.edges[0].node.merchandise.product.title;
  }, [cart]);

  return {
    totalQuantity,
    originTotalAmount,
    subTotalAmount,
    taxAmount,
    dutyAmount,
    shippingAmount,
    autoDiscountAmount,
    autoDiscountTitle,
    codeDiscountTitle,
    codeDiscountAmount,
    totalAmount,
    shippingTitle: shippingInfo?.title ?? "Shipping",
    orderName,
  };
}
